import { Link } from 'react-router-dom';
import { usePublicData, useEvents } from '../lib/hooks';
import { dateRangeText, inr } from '../lib/format';
import type { PortalEvent } from '../lib/types';
import { LOGO_SRC, Spinner } from '../components/ui';

/**
 * The home page for someone not signed in: the events that are on, each with its days, its food (book now, or "food
 * booking opens soon") and its own page with the programme. Signing in is only asked for when they book, or for passes.
 */
export default function Welcome() {
  const ev = useEvents();
  const { menu } = usePublicData();

  function summary(e: PortalEvent) {
    const meals = ev.current.filter((s) => s.event_id === e.id);
    const ids = new Set(meals.map((s) => s.id));
    const items = (menu.data ?? []).filter((m) => ids.has(m.sitting_id));
    const paid = items.filter((m) => !m.is_free && m.adult_price > 0).map((m) => m.adult_price);
    return {
      meals: meals.length,
      bookable: meals.length > 0 && e.booking_open,
      from: paid.length ? Math.min(...paid) : null,
      bhog: items.some((m) => m.is_free),
      when: e.starts_on && e.ends_on ? dateRangeText(e.starts_on, e.ends_on) : '',
    };
  }

  return (
    <div className="shell">
      <div className="app-bar">
        <span className="brand-chip"><img src={LOGO_SRC} alt="ONE10" style={{ height: 32 }} /></span>
        <Link className="btn ghost sm" to="/signin">Sign in</Link>
      </div>
      <main className="app-body">
        <div>
          <p className="app-eyebrow">ONE10 Food Pass</p>
          <h1 className="display" style={{ fontSize: 26, margin: '4px 0 0' }}>What's on at ONE10</h1>
          <p className="muted">Each event's programme, and its meals to book for your whole family. Pay online or by UPI, and show one QR at the counter.</p>
        </div>

        {!ev.ready ? <div className="empty"><Spinner /></div> : ev.live.length === 0 ? (
          <div className="card empty">
            <p className="display" style={{ fontSize: 20 }}>No event is on right now</p>
            <p>Each event's programme and meals appear here once the committee announces it.</p>
          </div>
        ) : ev.live.map((e) => {
          const s = summary(e);
          return (
            <div key={e.id} className="card wevent">
              <p className="app-eyebrow">{s.bookable ? 'Now booking' : s.meals ? 'On now' : 'Coming up'}</p>
              <p className="display" style={{ fontSize: 22, margin: 0 }}>{e.name}</p>
              <p className="muted" style={{ margin: 0 }}>
                {[s.when, s.meals ? `${s.meals} meal${s.meals === 1 ? '' : 's'}` : 'Food booking opens soon', s.from !== null ? `from ${inr(s.from)} a plate` : null,
                  s.bhog ? 'free Bhog' : null, s.meals && !s.bookable ? 'booking closed' : null].filter(Boolean).join(' · ')}
              </p>
              <div className="row">
                {s.bookable && <Link className="btn primary" to={`/book?event=${encodeURIComponent(e.id)}`}>See the meals and book ›</Link>}
                <Link className={`btn ${s.bookable ? 'ghost' : 'primary'}`} to={`/e/${encodeURIComponent(e.id)}`}>Programme and info ›</Link>
              </div>
            </div>
          );
        })}

        <div className="card stack" style={{ gap: 8 }}>
          <p><b>Booked already?</b> Sign in to see your family's meal passes and orders.</p>
          <Link className="btn ghost" style={{ justifySelf: 'start' }} to="/signin">Sign in</Link>
        </div>

        <p className="fine center">
          <Link to="/venues">Where to eat</Link> · <Link to="/live">Live board</Link>
        </p>
      </main>
    </div>
  );
}
